import { zodResolver } from '@hookform/resolvers/zod'
import {
  AlertCircle,
  Check,
  ChevronsUpDown,
  LoaderCircle,
  X,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { cn } from '../../lib/utils'
import type {
  ReportEmailRecipientOption,
  ReportEmailRecipientsResult,
  ReportEmailSenderOption,
} from '../../services/reports.service'

import { FormError } from './FormError'
import { Badge } from './badge'
import { Button } from './button'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from './command'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './dialog'
import { Input } from './input'
import { Label } from './label'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { RadioGroup, RadioGroupItem } from './radio-group'
import { Textarea } from './textarea'

const sendEmailSchema = z
  .object({
    recipientIds: z
      .array(z.number().int().positive())
      .min(1, 'Selecciona al menos un destinatario'),
    subject: z
      .string()
      .trim()
      .min(1, 'El asunto es obligatorio')
      .max(150, 'El asunto no puede exceder 150 caracteres'),
    description: z
      .string()
      .trim()
      .max(1000, 'La descripción no puede exceder 1000 caracteres'),
    limitType: z.enum(['all', 'custom']),
    customLimit: z.string(),
  })
  .superRefine((value, context) => {
    if (
      value.limitType === 'custom' &&
      (!/^\d+$/.test(value.customLimit) || Number(value.customLimit) < 1)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['customLimit'],
        message: 'Ingresa una cantidad de registros válida',
      })
    }
  })

export type SendEmailPayload = z.infer<typeof sendEmailSchema>

interface SendEmailModalProps {
  isOpen: boolean
  onClose: () => void
  onSend: (payload: SendEmailPayload) => void | Promise<void>
  loadRecipients: (
    search: string,
    page: number,
  ) => Promise<ReportEmailRecipientsResult>
  title?: string
  dialogDescription?: string
  isSending?: boolean
  reportName?: string
}

const initialPayload: SendEmailPayload = {
  recipientIds: [],
  subject: '',
  description: '',
  limitType: 'all',
  customLimit: '',
}

export function SendEmailModal({
  isOpen,
  onClose,
  onSend,
  loadRecipients,
  title = 'Enviar por Email',
  dialogDescription = 'Selecciona usuarios del sistema y completa los datos del envío.',
  isSending = false,
  reportName,
}: SendEmailModalProps) {
  const [recipientPickerOpen, setRecipientPickerOpen] = useState(false)
  const [selectedRecipients, setSelectedRecipients] = useState<
    ReportEmailRecipientOption[]
  >([])
  const [recipientOptions, setRecipientOptions] = useState<
    ReportEmailRecipientOption[]
  >([])
  const [sender, setSender] = useState<ReportEmailSenderOption | null>(null)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false)
  const [recipientLoadError, setRecipientLoadError] = useState(false)
  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SendEmailPayload>({
    resolver: zodResolver(sendEmailSchema),
    defaultValues: initialPayload,
  })

  useEffect(() => {
    if (!isOpen) return
    reset(initialPayload)
    setSelectedRecipients([])
    setRecipientOptions([])
    setSender(null)
    setSearch('')
    setDebouncedSearch('')
    setPage(1)
    setLastPage(1)
    setRecipientLoadError(false)
    setRecipientPickerOpen(false)
  }, [isOpen, reset])

  useEffect(() => {
    if (!isOpen) return
    const timeout = window.setTimeout(() => {
      const nextSearch = search.trim()
      setDebouncedSearch((currentSearch) => {
        if (currentSearch === nextSearch) return currentSearch
        setRecipientOptions([])
        setPage(1)
        return nextSearch
      })
    }, 250)

    return () => window.clearTimeout(timeout)
  }, [isOpen, search])

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    setIsLoadingRecipients(true)
    setRecipientLoadError(false)

    void loadRecipients(debouncedSearch, page)
      .then((result) => {
        if (cancelled) return
        setSender(result.sender)
        setLastPage(result.last_page)
        setRecipientOptions((current) => {
          const next = page === 1 ? result.data : [...current, ...result.data]
          return Array.from(
            new Map(next.map((recipient) => [recipient.id, recipient])).values(),
          )
        })
      })
      .catch(() => {
        if (!cancelled) setRecipientLoadError(true)
      })
      .finally(() => {
        if (!cancelled) setIsLoadingRecipients(false)
      })

    return () => {
      cancelled = true
    }
  }, [debouncedSearch, isOpen, loadRecipients, page])

  const updateRecipients = (recipients: ReportEmailRecipientOption[]) => {
    setSelectedRecipients(recipients)
    setValue(
      'recipientIds',
      recipients.map((recipient) => recipient.id),
      { shouldDirty: true, shouldValidate: true },
    )
  }

  const toggleRecipient = (recipient: ReportEmailRecipientOption) => {
    const selected = selectedRecipients.some(({ id }) => id === recipient.id)
    updateRecipients(
      selected
        ? selectedRecipients.filter(({ id }) => id !== recipient.id)
        : [...selectedRecipients, recipient],
    )
    setRecipientPickerOpen(false)
    setSearch('')
    setDebouncedSearch('')
    setPage(1)
  }

  const isProcessing = isSending || isSubmitting
  const limitType = watch('limitType')
  const senderUnavailable = sender !== null && !sender.configured

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto p-6 sm:max-w-[620px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>
        {reportName && (
          <p className="rounded-md bg-slate-50 px-3 py-2 text-sm">
            <span className="font-medium">Reporte:</span> {reportName}
          </p>
        )}

        <form
          className="space-y-4 pt-2"
          onSubmit={handleSubmit((payload) => onSend(payload))}
          noValidate
        >
          <div className="space-y-2">
            <Label>Destinatarios</Label>
            <Popover
              open={recipientPickerOpen}
              onOpenChange={setRecipientPickerOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between font-normal"
                  disabled={isProcessing || recipientLoadError}
                >
                  {selectedRecipients.length > 0
                    ? `${selectedRecipients.length} usuario${selectedRecipients.length === 1 ? '' : 's'} seleccionado${selectedRecipients.length === 1 ? '' : 's'}`
                    : 'Seleccionar usuarios'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Buscar por nombre o correo..."
                    value={search}
                    onValueChange={setSearch}
                  />
                  <CommandList className="max-h-[260px] overflow-y-auto">
                    {isLoadingRecipients && recipientOptions.length === 0 ? (
                      <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                        <LoaderCircle className="h-4 w-4 animate-spin" />
                        Cargando usuarios...
                      </div>
                    ) : (
                      <>
                        <CommandEmpty>No se encontraron usuarios.</CommandEmpty>
                        {recipientOptions.map((recipient) => {
                          const selected = selectedRecipients.some(
                            ({ id }) => id === recipient.id,
                          )
                          return (
                            <CommandItem
                              key={recipient.id}
                              value={String(recipient.id)}
                              className="h-[52px]"
                              onSelect={() => toggleRecipient(recipient)}
                            >
                              <Check
                                className={cn(
                                  'h-4 w-4',
                                  selected ? 'opacity-100' : 'opacity-0',
                                )}
                              />
                              <span className="min-w-0">
                                <span className="block truncate font-medium">
                                  {recipient.name}
                                </span>
                                <span className="block truncate text-xs text-muted-foreground">
                                  {recipient.email}
                                </span>
                              </span>
                            </CommandItem>
                          )
                        })}
                        {page < lastPage && (
                          <Button
                            type="button"
                            variant="ghost"
                            className="w-full rounded-none"
                            disabled={isLoadingRecipients}
                            onClick={() => setPage((current) => current + 1)}
                          >
                            {isLoadingRecipients ? 'Cargando...' : 'Cargar más'}
                          </Button>
                        )}
                      </>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {selectedRecipients.length > 0 && (
              <div className="flex max-h-24 flex-wrap gap-2 overflow-y-auto rounded-md border border-border p-2">
                {selectedRecipients.map((recipient) => (
                  <Badge key={recipient.id} variant="secondary" className="gap-1">
                    {recipient.name}
                    <button
                      type="button"
                      aria-label={`Quitar ${recipient.name}`}
                      onClick={() => toggleRecipient(recipient)}
                      disabled={isProcessing}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            <FormError
              id="send-email-recipients-error"
              message={errors.recipientIds?.message}
            />
            {recipientLoadError && (
              <p className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                No fue posible cargar los usuarios disponibles.
              </p>
            )}
          </div>

          <div
            className={cn(
              'rounded-md border px-3 py-2 text-sm',
              senderUnavailable
                ? 'border-destructive/40 bg-destructive/5 text-destructive'
                : 'border-border bg-muted/40 text-muted-foreground',
            )}
          >
            {sender === null ? (
              <span>Cargando remitente de la sucursal activa...</span>
            ) : sender.configured ? (
              <>
                <span className="font-medium text-foreground">Remitente:</span>{' '}
                {sender.name} &lt;{sender.address}&gt; · {sender.sucursal_name}
              </>
            ) : (
              <span>
                Configura el correo remitente de {sender.sucursal_name} antes de
                enviar reportes.
              </span>
            )}
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Registros del archivo</legend>
            <Controller
              name="limitType"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="gap-3"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id="send-email-all" value="all" />
                    <Label htmlFor="send-email-all" className="font-normal">
                      Todos
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id="send-email-custom" value="custom" />
                    <Label htmlFor="send-email-custom" className="font-normal">
                      Primeros
                    </Label>
                    <Input
                      className="w-28"
                      type="number"
                      min={1}
                      aria-label="Cantidad de registros"
                      disabled={limitType !== 'custom'}
                      {...register('customLimit')}
                    />
                  </div>
                </RadioGroup>
              )}
            />
            <FormError
              id="send-email-limit-error"
              message={errors.customLimit?.message}
            />
          </fieldset>

          <div className="space-y-2">
            <Label htmlFor="send-email-subject">Asunto</Label>
            <Input
              id="send-email-subject"
              placeholder="Escribe el asunto del correo"
              aria-describedby="send-email-subject-error"
              {...register('subject')}
            />
            <FormError
              id="send-email-subject-error"
              message={errors.subject?.message}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="send-email-description">Descripción (opcional)</Label>
            <Textarea
              id="send-email-description"
              rows={5}
              placeholder="Añade un mensaje para los destinatarios"
              aria-describedby="send-email-description-error"
              {...register('description')}
            />
            <FormError
              id="send-email-description-error"
              message={errors.description?.message}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                isProcessing ||
                sender === null ||
                senderUnavailable ||
                recipientLoadError
              }
            >
              {isProcessing ? 'Registrando...' : 'Enviar'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
